import { QueryClient } from '@tanstack/react-query'
import { Preferences } from '@capacitor/preferences'
import { CapacitorCookies } from '@capacitor/core'
import { URLS } from '@assets/environment/urls'
import { LocalStorageKeys } from '@shared/constants/LocalStorageKeys'
import { authTokenStorage } from '@shared/lib/authTokenStorage'
import { clearPendingAuthState, requestFreshKeycloakLogin } from '@shared/lib/authRedirect'
import { VaultProxy } from '@shared/lib/vault'

const LEGACY_AUTH_STORAGE_KEYS = [
	LocalStorageKeys.token,
	LocalStorageKeys.accessToken,
	LocalStorageKeys.refreshToken,
	LocalStorageKeys.id,
	LocalStorageKeys.workoutRedirectURL,
	LocalStorageKeys.coachChatVisibleSessionId,
]

const AUTH_VAULT_STORAGE_KEYS = [
	...LEGACY_AUTH_STORAGE_KEYS,
	`${LocalStorageKeys.accessToken}:storedAt`,
	`${LocalStorageKeys.accessToken}:generation`,
]

const getKeycloakCookieUrls = (): string[] => {
	const urls = new Set<string>()

	try {
		const keycloakUrl = new URL(URLS.KC_URL)
		const baseUrl = keycloakUrl.href.endsWith('/') ? keycloakUrl.href : `${keycloakUrl.href}/`

		urls.add(keycloakUrl.origin)
		urls.add(baseUrl)
		urls.add(new URL(`realms/${URLS.KC_REALM}`, baseUrl).href)
	} catch {
		// Invalid auth env should not block local cleanup.
	}

	return Array.from(urls)
}

const clearLegacyAuthStorage = async (): Promise<void> => {
	if (typeof window !== 'undefined') {
		LEGACY_AUTH_STORAGE_KEYS.forEach(key => {
			window.localStorage.removeItem(key)
			window.sessionStorage.removeItem(key)
		})
	}

	await Promise.allSettled(LEGACY_AUTH_STORAGE_KEYS.map(key => Preferences.remove({ key })))
}

const clearAuthVaultStorage = async (): Promise<void> => {
	await Promise.allSettled(AUTH_VAULT_STORAGE_KEYS.map(key => VaultProxy.removeItem(key)))
}

const clearCacheStorage = async (): Promise<void> => {
	if (typeof window === 'undefined' || !('caches' in window)) return

	const keys = await window.caches.keys()
	await Promise.all(keys.map(key => window.caches.delete(key)))
}

const clearKeycloakCookies = async (): Promise<void> => {
	const keycloakCookieUrls = getKeycloakCookieUrls()
	await Promise.allSettled(keycloakCookieUrls.map(url => CapacitorCookies.clearCookies({ url })))
}

type ClearLoginSessionCacheOptions = {
	clearAuthTokens?: boolean
}

export const clearLoginSessionCache = async (
	queryClient?: QueryClient,
	options: ClearLoginSessionCacheOptions = {}
): Promise<void> => {
	queryClient?.clear()
	clearPendingAuthState()
	requestFreshKeycloakLogin()

	const cleanupOperations: Promise<unknown>[] = [
		clearLegacyAuthStorage(),
		clearAuthVaultStorage(),
		clearCacheStorage(),
		clearKeycloakCookies(),
	]
	if (options.clearAuthTokens !== false) {
		cleanupOperations.unshift(authTokenStorage.clearTokens())
	}

	await Promise.allSettled(cleanupOperations)
}
